import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import { useGetFieldAdminHSDayQuery, useReopenFieldAdminHSDayMutation } from "../fieldAdminApi";
import { CARD_BG, BORDER, AMBER } from "../../../Global/colors";
import { FAPageState } from "../FAStates";
import { parseLocalDate, OK_GREEN_TEXT, FONT_FAMILY, formatTimeOfDay } from "../designTokens";
import { computeStepMeta, WorkflowTileList, SHORT_NAMES } from "./WorkflowSummary";
import { FAPageShell, FAPageHeader, RecordRow } from "../FALayout";

export default function Hub() {
  const navigate = useNavigate();
  const { data: day, isSuccess, isError, error, refetch } = useGetFieldAdminHSDayQuery();
  const [reopenDay, { isLoading: isReopening }] = useReopenFieldAdminHSDayMutation();

  if (!isSuccess) return <FAPageState isError={isError} error={error} onRetry={refetch} count={5} />;

  const steps = computeStepMeta(day);
  const completedCount = steps.filter((s) => s.done).length;
  const nextStep = steps.find((s) => !s.done);
  const progressPct = Math.round((completedCount / steps.length) * 100);

  return (
      <FAPageShell>
        <FAPageHeader title="Health & Safety" sub="Daily workflow · auditable per the H&S file (DoL)." sx={{ mb: "22px" }} />

        {/* Unlike the stock close-out's banner, this one names the way back — the H&S day
            lock is soft by design, and the FA can lift it themselves. */}
        {day.locked ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap", backgroundColor: "rgba(34,197,94,0.06)", border: "1px solid rgba(34,197,94,0.22)", borderRadius: "8px", p: "10px 14px", mb: "18px" }}>
            <LockOutlinedIcon sx={{ color: OK_GREEN_TEXT, fontSize: "14px", flexShrink: 0 }} />
            <Typography sx={{ color: OK_GREEN_TEXT, fontSize: "0.78rem", flexGrow: 1, minWidth: 0 }}>
              Signed off and locked. These sections can be viewed but not edited.
            </Typography>
            <Box
              component="button"
              type="button"
              disabled={isReopening}
              onClick={() => reopenDay()}
              sx={{
                display: "flex", alignItems: "center", gap: "6px", flexShrink: 0,
                background: "none", border: "none", p: 0,
                cursor: isReopening ? "default" : "pointer", fontFamily: FONT_FAMILY,
                fontSize: "0.76rem", fontWeight: 700, color: OK_GREEN_TEXT,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <LockOpenOutlinedIcon sx={{ fontSize: "0.9rem" }} />
              {isReopening ? "Reopening…" : "Reopen day"}
            </Box>
          </Box>
        ) : null}

        {/* Hero progress card */}
        <Box sx={{ background: "linear-gradient(135deg, rgba(253,185,64,0.1), rgba(253,185,64,0.03))", border: "1px solid rgba(253,185,64,0.25)", borderRadius: "14px", p: "20px", mb: "22px" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", mb: "14px" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <TaskAltOutlinedIcon sx={{ color: "rgba(253,185,64,0.85)", fontSize: "0.95rem" }} />
              <Typography sx={{ fontSize: "0.74rem", fontWeight: 700, color: "rgba(253,185,64,0.85)", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Today's checklist
              </Typography>
            </Box>
            <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", flexShrink: 0 }}>
              {/* day.date is a bare DateField string, and `new Date("2026-08-12")` is
                  parsed as UTC midnight — which renders as the previous day at any
                  negative UTC offset. Appending a time forces local parsing, the same
                  form Home, Team, History and Vehicle already use. The timestamps below
                  are real DateTimeFields, so they need no such handling. */}
              {parseLocalDate(day.date).toLocaleDateString(undefined, { weekday: "short" })}
              {" · "}
              {parseLocalDate(day.date).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
              {/* opened_at, not started_at: started_at is auto_now_add and stamps when the
                  row appeared, which any GET can do. Absent until the day is opened. */}
              {day.opened_at ? (
                <>
                  {" · "}
                  {formatTimeOfDay(day.opened_at)}
                </>
              ) : null}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
            <Typography sx={{ fontSize: "2rem", fontWeight: 700, color: AMBER, lineHeight: 1 }}>{completedCount}</Typography>
            <Typography sx={{ fontSize: "1rem", fontWeight: 500, color: "rgba(255,255,255,0.55)" }}>of {steps.length}</Typography>
            <Typography sx={{ fontSize: "0.92rem", color: "rgba(255,255,255,0.75)", ml: "4px" }}>sections complete</Typography>
          </Box>
          <Box sx={{ mt: "12px", height: "8px", backgroundColor: "rgba(255,255,255,0.08)", borderRadius: "4px", overflow: "hidden" }}>
            <Box sx={{ height: "100%", width: `${progressPct}%`, background: `linear-gradient(90deg,${AMBER},#E0A020)`, borderRadius: "4px" }} />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: "10px", flexWrap: "wrap", gap: "8px" }}>
            <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.5)" }}>
              {completedCount === 0 ? "Nothing completed yet" : `${steps.filter((s) => s.done).map((s) => SHORT_NAMES[s.key]).join(" · ")} complete`}
            </Typography>
            {/* design: .hero-meta .step-info is 0.7-alpha white at weight 500, not amber */}
            <Typography sx={{ fontSize: "0.74rem", color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
              {day.locked ? "Day complete ✓" : nextStep ? `Next · ${nextStep.name}` : ""}
            </Typography>
          </Box>
        </Box>

        {/* Workflow tiles */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: "6px", mb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Workflow sequence
          </Typography>
          <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.4)" }}>{completedCount} of {steps.length} complete</Typography>
        </Box>

        <Box sx={{ mb: "24px" }}>
          <WorkflowTileList steps={steps} showStepNumbers locked={day.locked} />
        </Box>

        {/* Records & audit — both rows are real now. */}
        <Box sx={{ py: "6px", mb: "12px" }}>
          <Typography sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700, color: "rgba(255,255,255,0.4)" }}>
            Records & audit
          </Typography>
        </Box>
        <Box sx={{ backgroundColor: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: "12px", overflow: "hidden" }}>
          <RecordRow Icon={DescriptionOutlinedIcon} name="H&S history" sub="Date-grouped log of every daily checklist · range selectable on the history page" cta="View →" onClick={() => navigate("/field-admin/hs/history/")} />
          <RecordRow Icon={FileDownloadOutlinedIcon} name="PDF export" sub="Date-range · DoL-ready" cta="Export →" onClick={() => navigate("/field-admin/hs/export/")} isLast />
        </Box>
      </FAPageShell>
  );
}

